import { SharedGridStyleBaseColor } from "features/CenterContent/shared/MainContentGrid";
import { Itinerary } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/Itinerary";
import { useVehicleCancellationAction } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/VehicleCancellation/VehicleCancellation";
import { useVehicleUndoCancellationAction } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/VehicleCancellation/VehicleUndoCancellation";
import { useVehiclePhoneNumberAction } from "features/CenterContent/VehicleDetails/shared/VehiclePhoneNumber";
import { useMemo } from "react";
import { ExpandableInfoMessage } from "shared/components/feedback/InfoMessage/ExpandableInfoMessage/ExpandableInfoMessage";
import { NoDataMessage } from "shared/components/feedback/NoDataMessage/NoDataMessage";
import { Module } from "shared/components/Module";
import { BusGps } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/BusGps/BusGps";
import OperationalLog from "features/CenterContent/VehicleDetails/shared/OperationalLog";
import { Box, Text, VStack } from "shared/components/Spor";
import {
  AlternativeTransportItinerary,
  AlternativeTransportItineraryStop,
  AlternativeTransportVehicle,
  StopReference,
} from "shared/types/alternativeTransport";
import styled from "styled-components";
import { IncidentsAndStopsPanels } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/IncidentsAndStopsPanels";
import { PanelActions } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/ActionsOverflowMenu";
import {
  PanelHorizontalScroll,
  PanelScrollContent,
  PanelScrollArea,
  PanelModuleGrid,
  PanelHeaderDivider,
} from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/altTransportPanelLayout";

const BusModule = styled(Module).attrs({ size: "lg" })`
  height: 100%;
  min-height: 0;
  && {
    border: 1px solid ${({ theme }) => theme.colorOutlineSecondary};
    border-left: none;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
`;

interface AlternativeTransportRightPanelProps {
  vehicle: AlternativeTransportVehicle;
}

export const AlternativeTransportRightPanel = ({
  vehicle,
}: AlternativeTransportRightPanelProps) => {
  const trainNumberTurnicate =
    vehicle.trainIds.join(", ").length > 16
      ? `${vehicle.trainIds.join(", ").substring(0, 16)}...`
      : vehicle.trainIds.join(", ");

  const getDistributionStopPlace = (
    itinerary: AlternativeTransportItinerary | null,
  ) =>
    itinerary
      ? [...itinerary.stops, ...(itinerary.returnStops ?? [])]
          .filter((stop) => !!stop.stopReference.nsrStopPlace)
          .map((stop) => stop.stopReference.nsrStopPlace)
          .join(",")
      : "";

  const distributionStopReferences: Array<StopReference> = useMemo(() => {
    const stops = vehicle?.itinerary?.stops ?? [];
    const returnStops = vehicle?.itinerary?.returnStops ?? [];
    return [...stops, ...returnStops].map(
      (value: AlternativeTransportItineraryStop) => value.stopReference,
    );
  }, [vehicle]);

  const distributionStopPlaces = useMemo(
    () => getDistributionStopPlace(vehicle.itinerary),
    [vehicle],
  );

  const shiftId = vehicle.workShiftPlan?.shiftId;
  const departureNumber = `${shiftId ? `${shiftId}-` : ""}${trainNumberTurnicate}${
    trainNumberTurnicate && !shiftId ? " Operative" : ""
  }`;

  const specification = vehicle.isPartOfShuttlePlan ? "Pendel" : undefined;

  const phoneAction = useVehiclePhoneNumberAction({
    phoneNumberUpdate: vehicle.driverPhoneNumber,
    workShift: vehicle.workShiftPlan,
    id: vehicle.id,
    operatorUpdate: {
      operatorName: vehicle.operatorName,
      operatorId: vehicle.operatorId,
    },
  });
  const cancellationAction = useVehicleCancellationAction(vehicle);
  const undoCancellationAction = useVehicleUndoCancellationAction(vehicle);

  const overflowActions = [
    phoneAction,
    cancellationAction,
    undoCancellationAction,
  ].filter((action) => action !== null);

  return (
    <BusModule
      title={
        <>
          <Box as="span" whiteSpace="nowrap">
            Avgang:
          </Box>
          {departureNumber ? (
            <>
              {" "}
              <Box as="span" whiteSpace="nowrap">
                {departureNumber}
              </Box>
            </>
          ) : null}
        </>
      }
      specification={specification}
      action1={
        <PanelActions
          overflowActions={overflowActions}
          inlineActions={[phoneAction]}
        />
      }
    >
      <PanelHeaderDivider>
        <Itinerary alternativeTransport={vehicle} />
      </PanelHeaderDivider>
      <PanelHorizontalScroll>
        <PanelScrollContent>
          <PanelScrollArea>
            <PanelModuleGrid>
              <Module title="Trasé" disablePadding>
                <IncidentsAndStopsPanels
                  vehicle={vehicle}
                  distributionStopReferences={distributionStopReferences}
                />
              </Module>
              <VStack gap="12px" align="stretch">
                <Box flexShrink="0">
                  <Module title="Info fra Brudd">
                    {vehicle.notes.length > 0 ? (
                      <div style={{ width: "100%" }}>
                        <ExpandableInfoMessage
                          semantics="info"
                          title="Info fra Brudd"
                        >
                          {vehicle.notes.map((note) => (
                            <Text key={note}>{note}</Text>
                          ))}
                        </ExpandableInfoMessage>
                      </div>
                    ) : (
                      <NoDataMessage
                        panelStyle={SharedGridStyleBaseColor}
                        reason="Det finnes ikke hendelse denne bussen"
                      />
                    )}
                  </Module>
                </Box>
                <Module title="Logg">
                  <OperationalLog
                    vehicleId={vehicle.id}
                    logs={vehicle.logs}
                    distributionStopPlaces={distributionStopPlaces}
                    disableInternalScroll
                    disablePadding
                  />
                </Module>
                <Module title="GPS-data">
                  <BusGps vehicle={vehicle} />
                </Module>
              </VStack>
            </PanelModuleGrid>
          </PanelScrollArea>
        </PanelScrollContent>
      </PanelHorizontalScroll>
    </BusModule>
  );
};

export default AlternativeTransportRightPanel;
